import { getNestedValue } from "./getNestedValue";
import { parsePathParts } from "./parsePathParts";
import { setValueAtKey } from "./setValueAtKey";

/**
 * Recursively removes an item from an object or array at a specified path.
 *
 * @param obj - The object or array to mutate.
 * @param dataPath - The RFC 6901 JSON Pointer path to the property (e.g., "/a/b/c" or "/items/0" for arrays).
 * @returns A new object or array with the item removed at the specified path.
 */
export const removeByPath = <T extends Record<string, unknown> | unknown[]>(
  obj: T,
  dataPath: string,
): T => {
  const { firstPart, restParts } = parsePathParts(dataPath);

  const isArray = Array.isArray(obj);

  if (restParts.length === 0) {
    // Base case: last part, remove the property
    if (isArray) {
      const newArr = [...obj] as unknown[];
      const index = parseInt(firstPart, 10);
      if (!isNaN(index) && index < (obj as unknown[]).length && index >= 0) {
        newArr.splice(index, 1);
        return newArr as T;
      } else {
        return obj;
      }
    } else {
      const newObj = { ...obj } as Record<string, unknown>;
      if (firstPart in obj) {
        delete (newObj as Record<string, unknown>)[firstPart];
        return newObj as T;
      }
      return newObj as T;
    }
  } else {
    // Recursive case: get or create the nested value (with copy) and recurse
    const existingValue = obj[firstPart as keyof T];
    const newObj = (isArray ? [...obj] : { ...obj }) as Record<string, unknown> | unknown[];
    if (existingValue !== undefined) {
      const nested = getNestedValue(obj, firstPart, restParts);
      const recursiveResult = removeByPath(nested, `/${restParts.join("/")}`);
      setValueAtKey(newObj, firstPart, recursiveResult, isArray);
    }
    return newObj as T;
  }
};
